import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { label: "Home", to: "/", slug: "home" },
  { label: "Services", to: "/services", slug: "services" },
  { label: "Cisco Partner", to: "/cisco-partner", slug: "cisco" },
  { label: "About", to: "/about", slug: "about" },
  { label: "Work", to: "/work", slug: "work" },
  { label: "Contact", to: "/contact", slug: "contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || pathname !== "/";

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid
          ? "backdrop-blur-xl bg-[#0A0A0B]/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-20 flex items-center justify-between">
        <Link data-testid="logo-home" to="/" className="flex items-center gap-2 group">
          <span className="h-3 w-3 rounded-full bg-accent shadow-[0_0_20px_var(--accent-glow)] group-hover:scale-125 transition-transform duration-300" />
          <span className="font-heading font-extrabold tracking-tight text-lg uppercase">
            Quadrim<span className="text-accent">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 font-mono text-xs uppercase tracking-[0.15em]">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.slug}`}
              className={({ isActive }) =>
                `link-underline transition-colors duration-300 ${
                  isActive ? "text-accent" : "text-[#A1A1AA] hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="header-cta"
          onClick={() => navigate("/contact")}
          className="hidden md:flex items-center gap-2 rounded-full border border-white/15 hover:border-accent hover:bg-accent hover:text-black px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 group"
        >
          Start a project
          <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform duration-300" />
        </button>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#0A0A0B] border-b border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.slug}`}
                  className={({ isActive }) =>
                    `text-left font-heading text-2xl uppercase tracking-tight ${
                      isActive ? "text-accent" : "text-white"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <button
                data-testid="mobile-cta"
                onClick={() => navigate("/contact")}
                className="mt-2 rounded-full bg-accent text-black px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] w-fit"
              >
                Start a project
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
